import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, BarChart, Bar,
} from "recharts";
import {
  Users, FileText, Megaphone, Zap,
  MessageSquare, ArrowRight, Mail,
  Send, MessageCircle, UserPlus, Percent, Timer, BarChart3, CalendarDays,
  Plus, Trash2, LayoutDashboard, TrendingUp, Search, Download,
} from "lucide-react";
import { useLocation } from "wouter";

const PERIOD_OPTIONS = [
  { label: "7 dias", value: 7 },
  { label: "14 dias", value: 14 },
  { label: "30 dias", value: 30 },
  { label: "60 dias", value: 60 },
];

type Tone = "teal" | "accent" | "coral" | "violet" | "amber";

const toneStyles: Record<Tone, { bg: string; fg: string }> = {
  teal: {
    bg: "color-mix(in oklab, var(--brand-teal) 10%, transparent)",
    fg: "var(--brand-teal)",
  },
  accent: {
    bg: "var(--brand-accent-soft)",
    fg: "var(--brand-accent-dark)",
  },
  coral: {
    bg: "color-mix(in oklab, var(--brand-coral) 14%, transparent)",
    fg: "var(--brand-coral)",
  },
  violet: {
    bg: "color-mix(in oklab, var(--brand-violet) 14%, transparent)",
    fg: "var(--brand-violet)",
  },
  amber: {
    bg: "color-mix(in oklab, var(--brand-amber) 14%, transparent)",
    fg: "color-mix(in oklab, var(--brand-amber) 80%, black)",
  },
};

function SectionHead({
  icon: Icon,
  title,
  right,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-3.5">
      <div className="flex items-center gap-2.5 font-display font-semibold text-[15px] tracking-[-0.015em]">
        <span className="h-[22px] w-[22px] rounded-[5px] bg-muted flex items-center justify-center" style={{ color: "var(--brand-teal)" }}>
          <Icon className="h-3 w-3" />
        </span>
        {title}
      </div>
      {right}
    </div>
  );
}

function Pill({ children, live }: { children: React.ReactNode; live?: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-full font-mono text-[10px] tracking-[0.05em] uppercase border"
      style={
        live
          ? {
              color: "var(--brand-accent-dark)",
              borderColor: "color-mix(in oklab, var(--brand-accent) 30%, transparent)",
              background: "var(--brand-accent-soft)",
            }
          : {
              color: "var(--brand-fog)",
              borderColor: "var(--brand-line)",
              background: "var(--card)",
            }
      }
    >
      {children}
    </span>
  );
}

function RangeTabs({
  value,
  onChange,
  options,
}: {
  value: number;
  onChange: (v: number) => void;
  options: { label: string; value: number }[];
}) {
  return (
    <div className="inline-flex gap-0.5 bg-muted p-[3px] rounded-[7px] font-mono text-[11px] tracking-[0.04em]">
      {options.map((opt) => {
        const on = opt.value === value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-[11px] py-[5px] rounded-[5px] border-0 cursor-pointer transition-colors ${
              on ? "bg-card text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.05)]" : ""
            }`}
            style={!on ? { color: "var(--brand-fog)", background: "transparent" } : {}}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function KPICard({
  label,
  value,
  footer,
  icon: Icon,
  tone,
  isLoading,
  onClick,
}: {
  label: string;
  value: string | number;
  footer?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: Tone;
  isLoading?: boolean;
  onClick?: () => void;
}) {
  const s = toneStyles[tone];
  return (
    <div
      onClick={onClick}
      className={`bg-card border rounded-[10px] p-[18px] flex flex-col gap-3.5 transition-colors ${
        onClick ? "cursor-pointer hover:border-[color:var(--brand-teal-soft)]" : ""
      }`}
      style={{ borderColor: "var(--brand-line)" }}
    >
      <div className="flex justify-between items-start gap-2.5">
        <div className="text-xs font-medium" style={{ color: "var(--brand-fog)" }}>
          {label}
        </div>
        <div
          className="h-8 w-8 rounded-lg flex items-center justify-center flex-none"
          style={{ background: s.bg, color: s.fg }}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
      {isLoading ? (
        <div className="h-8 w-16 bg-muted animate-pulse rounded" />
      ) : (
        <div className="font-display font-semibold text-[32px] tracking-[-0.03em] leading-none text-foreground">
          {value}
        </div>
      )}
      {footer && (
        <div
          className="text-[11px] font-mono tracking-[0.04em] uppercase flex items-center gap-1.5"
          style={{ color: "var(--brand-fog)" }}
        >
          <span
            className="w-[5px] h-[5px] rounded-full opacity-40"
            style={{ background: "currentColor" }}
          />
          {footer}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  footer,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string | number;
  footer: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: Tone;
}) {
  const s = toneStyles[tone];
  const longValue = typeof value === "string" && value.length > 3;
  return (
    <div
      className="bg-card border rounded-[9px] px-3.5 py-3.5 flex flex-col gap-2 min-w-0"
      style={{ borderColor: "var(--brand-line)" }}
    >
      <div className="flex justify-between items-center gap-2">
        <div
          className="text-[11px] leading-tight truncate"
          style={{ color: "var(--brand-fog)" }}
        >
          {label}
        </div>
        <div
          className="h-[22px] w-[22px] rounded-[5px] flex items-center justify-center flex-none"
          style={{ background: s.bg, color: s.fg }}
        >
          <Icon className="h-3 w-3" />
        </div>
      </div>
      <div
        className="font-display font-semibold tracking-[-0.025em] leading-none text-foreground"
        style={{ fontSize: longValue ? 16 : 22, letterSpacing: longValue ? "-0.01em" : undefined }}
      >
        {value}
      </div>
      <div
        className="text-[10px] font-mono tracking-[0.04em] uppercase truncate"
        style={{ color: "var(--brand-fog)" }}
      >
        {footer}
      </div>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div
        className="border rounded-lg shadow-lg p-3 text-xs space-y-1"
        style={{ background: "var(--card)", borderColor: "var(--brand-line)" }}
      >
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload.map((p: any) => (
          <div key={p.name} className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span style={{ color: "var(--brand-fog)" }}>{p.name}:</span>
            <span className="font-medium">{p.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, Tone> = {
    draft: "violet",
    scheduled: "amber",
    sending: "teal",
    sent: "accent",
    failed: "coral",
  };
  const labels: Record<string, string> = {
    draft: "Rascunho",
    scheduled: "Agendada",
    sending: "Enviando",
    sent: "Enviada",
    failed: "Falhou",
  };
  const tone = map[status] ?? "violet";
  const s = toneStyles[tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-full font-mono text-[10px] tracking-[0.05em] uppercase border"
      style={{
        color: s.fg,
        background: s.bg,
        borderColor: "color-mix(in oklab, currentColor 24%, transparent)",
      }}
    >
      {labels[status] ?? status}
    </span>
  );
}

export default function WhatsAppDashboard() {
  const [days, setDays] = useState(30);
  const { data: stats, isLoading } = trpc.dashboard.stats.useQuery();
  const { data: emailStats } = trpc.sendgrid.history.globalStats.useQuery();
  const { data: waStats } = trpc.dashboard.whatsappStats.useQuery({ days });
  const { data: waDaily = [] } = trpc.dashboard.whatsappDaily.useQuery({ days });
  const { data: eventStats = [] } = trpc.customEvents.stats.useQuery({ days });
  const { data: allEvents = [] } = trpc.customEvents.list.useQuery();
  const createEvent = trpc.customEvents.create.useMutation();
  const deleteEvent = trpc.customEvents.delete.useMutation();
  const utils = trpc.useUtils();
  const [, setLocation] = useLocation();
  const [newEventName, setNewEventName] = useState("");
  const [showEventForm, setShowEventForm] = useState(false);

  const formatResponseTime = (minutes: number | null | undefined) => {
    if (minutes === null || minutes === undefined) return "—";
    if (minutes < 60) return `${minutes}min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) return;
    await createEvent.mutateAsync({ name: newEventName.trim() });
    setNewEventName("");
    setShowEventForm(false);
    utils.customEvents.list.invalidate();
    utils.customEvents.stats.invalidate();
  };

  const handleDeleteEvent = async (id: number) => {
    await deleteEvent.mutateAsync({ id });
    utils.customEvents.list.invalidate();
    utils.customEvents.stats.invalidate();
  };

  const kpis: {
    label: string;
    value: string | number;
    sub: string;
    icon: React.ComponentType<{ className?: string }>;
    tone: Tone;
    path: string;
  }[] = [
    {
      label: "Total de Contatos",
      value: stats?.totalContacts ?? 0,
      sub: `${stats?.activeContacts ?? 0} ativos`,
      icon: Users,
      tone: "teal",
      path: "/contacts",
    },
    {
      label: "Templates",
      value: stats?.totalTemplates ?? 0,
      sub: `${stats?.approvedTemplates ?? 0} aprovados`,
      icon: FileText,
      tone: "violet",
      path: "/templates",
    },
    {
      label: "Campanhas",
      value: stats?.totalCampaigns ?? 0,
      sub: `${stats?.sentCampaigns ?? 0} enviadas`,
      icon: Megaphone,
      tone: "coral",
      path: "/broadcast",
    },
    {
      label: "Automações Ativas",
      value: stats?.activeAutomations ?? 0,
      sub: "em execução",
      icon: Zap,
      tone: "accent",
      path: "/automations",
    },
    {
      label: "E-mails Enviados",
      value: emailStats?.sent ?? 0,
      sub: `${emailStats?.opened ?? 0} abertos`,
      icon: Mail,
      tone: "amber",
      path: "/emails",
    },
  ];

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-7">
        {/* ─── Topbar ──────────────────────────────── */}
        <div className="flex justify-between items-start gap-5 flex-wrap">
          <div>
            <h1 className="font-display font-semibold text-[26px] tracking-[-0.025em] leading-none m-0">
              Dashboard
            </h1>
            <div className="text-[13px] mt-1.5" style={{ color: "var(--brand-fog)" }}>
              Visão geral da sua automação de conversação
            </div>
          </div>
          <div className="flex gap-2.5 items-center flex-wrap">
            <div
              className="hidden md:flex items-center gap-2 bg-card border rounded-lg px-3 py-2 w-[240px] text-[12px]"
              style={{ borderColor: "var(--brand-line)", color: "var(--brand-fog)" }}
            >
              <Search className="h-3.5 w-3.5" />
              <span>Buscar contatos, campanhas...</span>
            </div>
            <Button
              variant="outline"
              className="font-display h-auto py-[9px] px-3.5 text-[13px] rounded-[7px]"
              style={{ borderColor: "var(--brand-line)" }}
              onClick={() => setLocation("/reports")}
            >
              <Download className="h-3.5 w-3.5" />
              Exportar
            </Button>
            <Button
              className="font-display h-auto py-[9px] px-3.5 text-[13px] rounded-[7px]"
              onClick={() => setLocation("/broadcast")}
            >
              <Plus className="h-3.5 w-3.5" />
              Nova campanha
            </Button>
          </div>
        </div>

        {/* ─── Visão geral (KPI) ───────────────────── */}
        <section>
          <SectionHead icon={LayoutDashboard} title="Visão geral" right={<Pill>últimos 30 dias</Pill>} />
          <div className="grid gap-3.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {kpis.map((k) => (
              <KPICard
                key={k.label}
                label={k.label}
                value={k.value}
                footer={k.sub}
                icon={k.icon}
                tone={k.tone}
                isLoading={isLoading}
                onClick={() => setLocation(k.path)}
              />
            ))}
          </div>
        </section>

        {/* ─── Estatísticas WhatsApp ───────────────── */}
        <section>
          <SectionHead
            icon={TrendingUp}
            title="Estatísticas WhatsApp"
            right={<RangeTabs value={days} onChange={setDays} options={PERIOD_OPTIONS} />}
          />
          <div className="grid gap-2.5 grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
            <StatCard
              label="Contatos Ativos"
              value={waStats?.contactsInteracted ?? 0}
              footer={`nos últimos ${days}d`}
              icon={MessageCircle}
              tone="teal"
            />
            <StatCard
              label="Msgs Recebidas"
              value={waStats?.messagesReceived ?? 0}
              footer="inbound"
              icon={MessageSquare}
              tone="teal"
            />
            <StatCard
              label="Msgs Enviadas"
              value={waStats?.messagesSent ?? 0}
              footer="outbound"
              icon={Send}
              tone="accent"
            />
            <StatCard
              label="Novos Contatos"
              value={waStats?.newContacts ?? 0}
              footer="no período"
              icon={UserPlus}
              tone="violet"
            />
            <StatCard
              label="Interações/Contato"
              value={waStats?.interactionsPerContact ?? 0}
              footer="média"
              icon={BarChart3}
              tone="coral"
            />
            <StatCard
              label="Taxa de Resposta"
              value={`${waStats?.responseRate ?? 0}%`}
              footer="respondidas"
              icon={Percent}
              tone="accent"
            />
            <StatCard
              label="Tempo Médio Resp."
              value={formatResponseTime(waStats?.avgResponseMinutes)}
              footer="primeira resposta"
              icon={Timer}
              tone="amber"
            />
          </div>
        </section>

        {/* ─── Charts ──────────────────────────────── */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard
            icon={UserPlus}
            title="Novos Contatos por Dia"
            empty={waDaily.length === 0}
          >
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={waDaily} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--brand-line)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "var(--brand-fog)" }}
                  interval={Math.max(0, Math.floor(waDaily.length / 7) - 1)}
                />
                <YAxis tick={{ fontSize: 11, fill: "var(--brand-fog)" }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line
                  type="monotone"
                  dataKey="novosContatos"
                  name="Novos Contatos"
                  stroke="var(--brand-accent)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
            <Legend_ swatches={[{ color: "var(--brand-accent)", label: "Novos contatos" }]} />
          </ChartCard>

          <ChartCard
            icon={MessageSquare}
            title="Mensagens Enviadas / Recebidas"
            empty={waDaily.length === 0}
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={waDaily} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--brand-line)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "var(--brand-fog)" }}
                  interval={Math.max(0, Math.floor(waDaily.length / 7) - 1)}
                />
                <YAxis tick={{ fontSize: 11, fill: "var(--brand-fog)" }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="recebidas" name="Recebidas" fill="var(--brand-teal)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="enviadas" name="Enviadas" fill="var(--brand-accent)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <Legend_
              swatches={[
                { color: "var(--brand-teal)", label: "Recebidas" },
                { color: "var(--brand-accent)", label: "Enviadas" },
              ]}
            />
          </ChartCard>
        </section>

        {/* ─── Campanhas Recentes + Eventos ────────── */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <BrandCard
            title="Campanhas Recentes"
            icon={Megaphone}
            right={
              <button
                onClick={() => setLocation("/broadcast")}
                className="text-[11px] font-mono tracking-[0.04em] uppercase hover:underline flex items-center gap-1"
                style={{ color: "var(--brand-teal)" }}
              >
                Ver todas <ArrowRight className="h-3 w-3" />
              </button>
            }
          >
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-muted animate-pulse rounded" />
                ))}
              </div>
            ) : stats?.recentCampaigns?.length === 0 ? (
              <EmptyState
                icon={Megaphone}
                title="Nenhuma campanha criada ainda"
                action={{
                  label: "Criar primeira campanha",
                  onClick: () => setLocation("/broadcast"),
                }}
              />
            ) : (
              <div className="space-y-2">
                {stats?.recentCampaigns?.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center justify-between p-3 rounded-[7px] border transition-colors hover:border-[color:var(--brand-teal-soft)]"
                    style={{ borderColor: "var(--brand-line)" }}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{campaign.name}</p>
                      <p className="text-[11px] font-mono tracking-[0.04em] uppercase mt-0.5" style={{ color: "var(--brand-fog)" }}>
                        {campaign.totalContacts} contatos
                      </p>
                    </div>
                    <StatusBadge status={campaign.status} />
                  </div>
                ))}
              </div>
            )}
          </BrandCard>

          <BrandCard
            title="Eventos Personalizados"
            icon={CalendarDays}
            right={
              <button
                onClick={() => setShowEventForm(!showEventForm)}
                className="text-[11px] font-mono tracking-[0.04em] uppercase hover:underline flex items-center gap-1"
                style={{ color: "var(--brand-teal)" }}
              >
                <Plus className="h-3 w-3" /> Criar
              </button>
            }
          >
            {showEventForm && (
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="Nome do evento (ex: Fez pagamento)"
                  value={newEventName}
                  onChange={(e) => setNewEventName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateEvent()}
                  className="flex-1 px-3 py-1.5 text-sm border rounded-md bg-background"
                  style={{ borderColor: "var(--brand-line)" }}
                />
                <Button size="sm" onClick={handleCreateEvent} disabled={!newEventName.trim()}>
                  Salvar
                </Button>
              </div>
            )}
            {eventStats.length === 0 && allEvents.length === 0 ? (
              <EmptyState
                icon={CalendarDays}
                title="Nenhum evento criado ainda"
                subtitle="Crie eventos para rastrear ações dos contatos"
              />
            ) : (
              <div className="space-y-2">
                {eventStats.map((evt) => (
                  <div
                    key={evt.id}
                    className="flex items-center justify-between p-3 rounded-[7px] border"
                    style={{ borderColor: "var(--brand-line)" }}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{evt.name}</p>
                      {evt.description && (
                        <p className="text-xs truncate" style={{ color: "var(--brand-fog)" }}>
                          {evt.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] tracking-[0.05em] font-semibold"
                        style={{
                          background: "var(--brand-mist)",
                          color: "var(--brand-ink)",
                        }}
                      >
                        {evt.count}
                      </span>
                      <button
                        onClick={() => handleDeleteEvent(evt.id)}
                        className="transition-colors hover:text-destructive"
                        style={{ color: "var(--brand-fog)" }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </BrandCard>
        </section>

        {/* ─── Ações rápidas ───────────────────────── */}
        <BrandCard title="Ações rápidas" icon={Zap}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {[
              { icon: Users, label: "Importar Contatos via CSV", path: "/contacts", tone: "teal" as Tone },
              { icon: FileText, label: "Criar Novo Template", path: "/templates", tone: "violet" as Tone },
              { icon: Megaphone, label: "Nova Transmissão WhatsApp", path: "/broadcast", tone: "coral" as Tone },
              { icon: Zap, label: "Configurar Automação", path: "/automations", tone: "accent" as Tone },
              { icon: MessageSquare, label: "Progresso", path: "/progress", tone: "amber" as Tone },
            ].map((action) => {
              const s = toneStyles[action.tone];
              return (
                <button
                  key={action.path}
                  onClick={() => setLocation(action.path)}
                  className="flex items-center gap-3 p-3 rounded-[7px] border transition-colors hover:border-[color:var(--brand-teal-soft)] text-left group"
                  style={{ borderColor: "var(--brand-line)" }}
                >
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: s.bg, color: s.fg }}
                  >
                    <action.icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium">{action.label}</span>
                  <ArrowRight
                    className="h-3.5 w-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: "var(--brand-fog)" }}
                  />
                </button>
              );
            })}
          </div>
        </BrandCard>
      </div>
    </DashboardLayout>
  );
}

// ─── Supporting bits ──────────────────────────────────────────────────────

function BrandCard({
  title,
  icon: Icon,
  right,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="bg-card border rounded-[12px] p-5 flex flex-col gap-4 min-w-0"
      style={{ borderColor: "var(--brand-line)" }}
    >
      <div className="flex justify-between items-start gap-3.5">
        <div className="flex items-center gap-2.5 font-display font-semibold text-[14px] tracking-[-0.015em]">
          <span
            className="h-[22px] w-[22px] rounded-[5px] bg-muted flex items-center justify-center"
            style={{ color: "var(--brand-teal)" }}
          >
            <Icon className="h-3 w-3" />
          </span>
          {title}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function ChartCard({
  title,
  icon,
  children,
  empty,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  empty?: boolean;
}) {
  const Icon = icon;
  return (
    <div
      className="bg-card border rounded-[12px] p-5 flex flex-col gap-4 min-w-0"
      style={{ borderColor: "var(--brand-line)" }}
    >
      <div className="flex justify-between items-start gap-3.5">
        <div className="flex items-center gap-2.5 font-display font-semibold text-[14px] tracking-[-0.015em]">
          <span
            className="h-[22px] w-[22px] rounded-[5px] bg-muted flex items-center justify-center"
            style={{ color: "var(--brand-teal)" }}
          >
            <Icon className="h-3 w-3" />
          </span>
          {title}
        </div>
      </div>
      {empty ? (
        <div
          className="flex items-center justify-center h-[240px] text-sm"
          style={{ color: "var(--brand-fog)" }}
        >
          Sem dados no período selecionado
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function Legend_({ swatches }: { swatches: { color: string; label: string }[] }) {
  return (
    <div
      className="flex gap-3.5 text-[11px] font-mono tracking-[0.04em] uppercase justify-center"
      style={{ color: "var(--brand-fog)" }}
    >
      {swatches.map((s) => (
        <div key={s.label} className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: s.color }} />
          {s.label}
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  subtitle,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="text-center py-8" style={{ color: "var(--brand-fog)" }}>
      <Icon className="h-8 w-8 mx-auto mb-2 opacity-30" />
      <p className="text-sm">{title}</p>
      {subtitle && <p className="text-xs mt-1">{subtitle}</p>}
      {action && (
        <button
          onClick={action.onClick}
          className="text-[11px] font-mono tracking-[0.04em] uppercase mt-2 hover:underline"
          style={{ color: "var(--brand-teal)" }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
